import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ReportTemplate,
  GeneratedReport,
  ScheduledReport,
  ReportBuilderData,
  ReportMetric,
  ReportStats,
} from '../types/reports.types';

// API Configuration
const API_BASE_URL = '/api';

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Create headers with authentication
 */
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Handle API response
 */
const handleAPIResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData: any = await response.json().catch(() => ({
      error: 'Request failed',
      message: response.statusText,
    }));
    throw new Error((errorData as any)?.error || (errorData as any)?.message || 'API request failed');
  }

  const data: any = await response.json();
  return data.data || data;
};

// Hooks
export const useReportTemplates = () => {
  return useQuery({
    queryKey: ['report-templates'],
    queryFn: async (): Promise<ReportTemplate[]> => {
      const response = await fetch(`${API_BASE_URL}/reports/templates`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<ReportTemplate[]>(response);
    },
    staleTime: 60000,
  });
};

export const useReports = (page = 1, perPage = 10) => {
  return useQuery({
    queryKey: ['reports', page, perPage],
    queryFn: async (): Promise<{ reports: GeneratedReport[]; total: number }> => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: perPage.toString(),
      });

      const response = await fetch(`${API_BASE_URL}/reports?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      
      const data = await handleAPIResponse<any>(response);
      return {
        reports: data.reports || [],
        total: data.total || 0,
      };
    },
    staleTime: 10000,
  });
};

export const useScheduledReports = () => {
  return useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: async (): Promise<ScheduledReport[]> => {
      const response = await fetch(`${API_BASE_URL}/reports/scheduled`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<ScheduledReport[]>(response);
    },
    staleTime: 30000,
  });
};

export const useReportMetrics = () => {
  return useQuery({
    queryKey: ['report-metrics'],
    queryFn: async (): Promise<ReportMetric[]> => {
      const response = await fetch(`${API_BASE_URL}/reports/metrics`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<ReportMetric[]>(response);
    },
    staleTime: 60000,
  });
};

export const useGenerateReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ReportBuilderData) => {
      const response = await fetch(`${API_BASE_URL}/reports/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleAPIResponse<GeneratedReport>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report generation started');
    },
    onError: () => toast.error('Failed to generate report'),
  });
};

export const useDownloadReport = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      // Get fresh token at query execution time
      const token = getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/reports/${id}/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      return response.blob();
    },
    onSuccess: () => toast.success('Download started'),
    onError: () => toast.error('Download failed'),
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE_URL}/reports/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<void>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report deleted');
    },
    onError: () => toast.error('Failed to delete report'),
  });
};

export const useScheduleReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`${API_BASE_URL}/reports/schedule`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleAPIResponse<ScheduledReport>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success('Report scheduled');
    },
    onError: () => toast.error('Failed to schedule report'),
  });
};

export const useUpdateSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await fetch(`${API_BASE_URL}/reports/schedule/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleAPIResponse<void>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success('Schedule updated');
    },
    onError: () => toast.error('Failed to update schedule'),
  });
};

export const useDeleteSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE_URL}/reports/schedule/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<void>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success('Schedule deleted');
    },
    onError: () => toast.error('Failed to delete schedule'),
  });
};
